/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { _isEmpty, _notNil } from '@/littledash';
import { useEffect, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import TableHeader from './Cells/TableHeader';
import Pagination from './PaginationNew';
import {
  calculateWindowHeight,
  cellProps as cellPropsStyling,
  getTableWithContextConfig,
  headerProps,
} from './Table.utils';
import { useTableContext } from './TableContext';

interface TableWithContextProps {
  tableRef: React.RefObject<HTMLDivElement>;
  testIdPrefix?: string;
  maxHeight?: number | undefined;
}

const TableWithContext: React.FC<TableWithContextProps> = ({ tableRef, testIdPrefix, maxHeight }) => {
  const [rowWidth, setRowWidth] = useState<number>();
  const [tableWidth, setTableWidth] = useState<number>();
  const { rowHeight, maxTableHeight } = getTableWithContextConfig(maxHeight);

  const tableContext = useTableContext();
  const { table } = tableContext;

  // Extract TanStack Table v8 data
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const page = table.getRowModel().rows; // In v8, we use rows directly
  const pageCount = table.getPageCount();
  const { pageIndex, pageSize } = table.getState().pagination;
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const gotoPage = table.setPageIndex;
  const previousPage = table.previousPage;
  const nextPage = table.nextPage;
  const setPageSize = table.setPageSize;

  // Calculate total columns width from table
  const totalColumnsWidth = headerGroups.reduce(
    (total, headerGroup) =>
      total + headerGroup.headers.reduce((groupTotal, header) => groupTotal + header.getSize(), 0),
    0
  );

  // Get additional props from context for backward compatibility
  const NoDataComponent = (tableContext as Record<string, unknown>).NoDataComponent;
  const pageSizes = ((tableContext as Record<string, unknown>).pageSizes as number[]) || [10, 25, 50, 100];
  const noDataMessage = ((tableContext as Record<string, unknown>).noDataMessage as string) || 'No rows to display';

  const _NoDataComponent = NoDataComponent instanceof Function ? NoDataComponent(tableContext) : NoDataComponent;

  useEffect(() => {
    const handleResize = () => {
      setTableWidth((tableRef?.current as HTMLDivElement)?.scrollWidth ?? totalColumnsWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize, {
      capture: false,
      passive: true,
    });
    return () => window.removeEventListener('resize', handleResize);
  }, [totalColumnsWidth, tableRef]);

  useEffect(() => {
    const currentWidth = totalColumnsWidth <= (tableWidth ?? 0) ? (tableWidth ?? totalColumnsWidth) : totalColumnsWidth;
    setRowWidth(currentWidth);
  }, [totalColumnsWidth, tableWidth]);

  return (
    <>
      <div
        className="ui__table"
        data-testid={`${testIdPrefix}__api-table`}
        data-test-component="TableWithContext"
        data-test-element="container"
      >
        <div className="table overflow-auto">
          <div
            data-testid={`${testIdPrefix}__table-container`}
            data-width-set={_notNil(rowWidth) ? 'true' : 'false'}
            style={{ width: rowWidth ?? undefined }}
          >
            <div className="thead" data-test-element="header" data-testid={`${testIdPrefix}__api-table-header`}>
              {headerGroups.map((headerGroup, groupIndex) => (
                <div className="tr" key={`headerGroup_${groupIndex}`}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHeader key={`header_${index}`} header={header} headerProps={headerProps} />
                  ))}
                </div>
              ))}
            </div>
            <div className="tbody" data-test-element="body" data-testid={`${testIdPrefix}__api-table-body`}>
              {!_isEmpty(rows) ? (
                <FixedSizeList
                  width={rowWidth || 0}
                  height={calculateWindowHeight(page.length, rowHeight, maxTableHeight) || 400}
                  itemCount={page.length}
                  itemSize={rowHeight}
                >
                  {RenderRow}
                </FixedSizeList>
              ) : (
                <>
                  {_NoDataComponent ? (
                    <div className="ma3">{_NoDataComponent}</div>
                  ) : (
                    <div className="flex flex-column white min-h5 justify-center items-center tc pa3 ma3 bg-light-gray">
                      <h2 className="tc pa3 ma3">{noDataMessage ?? 'No rows to display'}</h2>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {rows.length >= 1 && (
        <Pagination
          previousPage={previousPage}
          nextPage={nextPage}
          pageCount={pageCount}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizes={pageSizes}
          setPageSize={setPageSize}
          testPrefix={testIdPrefix}
        />
      )}
    </>
  );
};

const RenderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
  const { table } = useTableContext();
  const page = table.getRowModel().rows;
  const row = page[index];
  const isCheckboxCell = (meta: Record<string, unknown> | undefined) =>
    (meta?.className as string)?.includes('ui__selector-column');

  return (
    <div
      style={style}
      className={classNames('tr', {
        'bb b--moon-gray': index === page.length - 1,
      })}
      role="row"
      data-test-component="RenderRow"
      data-test-element="row-container"
      data-test-key={String(index)}
      data-testid={`api-table-row-${index}`}
    >
      {row.getVisibleCells().map((cell, columnIndex) => {
        const cellStyle = cellPropsStyling({}, { cell });
        const meta = cell.column.columnDef.meta as Record<string, unknown>;

        return (
          <div
            className={classNames('td', meta?.className as string | undefined)}
            key={cell.id}
            role="cell"
            data-test-component="RenderRow"
            data-test-element="cell"
            data-test-key={`( ${cell.column.id} , ${cell.row.id} )`}
            style={{
              ...(!isCheckboxCell(meta) && {
                ...cellStyle[1]?.style,
                flexBasis: cell.column.getSize(),
              }),
              minWidth: cell.column.columnDef.minSize,
              maxWidth:
                isCheckboxCell(meta) || (meta?.className as string)?.includes('ui__expander-column')
                  ? cell.column.getSize()
                  : undefined,
            }}
            data-testid={`api-table-column-${columnIndex}`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        );
      })}
    </div>
  );
};

export default TableWithContext;
